import React, { createContext, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import AppContext from './AppContext';
const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {

  const { baseUrl, authToken } = useContext(AppContext);

  const [profile, setProfile] = useState({
    name: 'Admin User',
    email: 'admin@journeyjunction.com',
    phone: '+92 300 1234567',
    role: 'Super Admin',
    avatar: 'https://i.pravatar.cc/150?img=12',
    joinedDate: '2024-01-15'
  });

  const [notificationPreferences, setNotificationPreferences] = useState({
    bookingNotifications: {
      inApp: true,
      email: true,
      description: 'Get notified when new bookings are made'
    },
    paymentNotifications: {
      inApp: true,
      email: false,
      description: 'Receive payment and transaction alerts'
    },
    systemAlerts: {
      inApp: true,
      email: true,
      description: 'Critical system and security notifications'
    },
    reviewNotifications: {
      inApp: true,
      email: false,
      description: 'New reviews and rating updates'
    },
    userActivity: {
      inApp: false,
      email: false,
      description: 'User registration and activity logs'
    }
  });

  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [isUpdatingNotifications, setIsUpdatingNotifications] = useState(false);

  const [toast, setToast] = useState({
    show: false,
    message: '',
    type: 'success'
  });

  const showToast = useCallback((message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  }, []);

  const updateProfile = useCallback(async (updatedData) => {
    setIsUpdatingProfile(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setProfile(prev => ({ ...prev, ...updatedData }));
      showToast('Profile updated successfully', 'success');
      return { success: true };
    } catch (error) {
      showToast('Failed to update profile', 'error');
      return { success: false, error };
    } finally {
      setIsUpdatingProfile(false);
    }
  }, [showToast]);

  const updatePassword = useCallback((passwordData) => {
    setIsUpdatingPassword(true);

    return axios.post(`${baseUrl}/admin/update-password`, {
      current_password: passwordData.currentPassword,
      new_password:     passwordData.newPassword,
    }, {
      headers: {
        Authorization: `Bearer ${authToken}`
      }
    })
    .then(response => {
      if (response.data.success) {
        showToast('Password updated successfully', 'success');
        return { success: true };
      } else {
        showToast(response.data.message || 'Update failed', 'error');
        return { success: false, message: response.data.message };
      }
    })
    .catch(error => {
      const msg = error.response?.data?.message || 'Failed to update password';
      showToast(msg, 'error');
      return { success: false, message: msg };
    })
    .finally(() => {
      setIsUpdatingPassword(false);
    });
  }, [baseUrl, authToken, showToast]);

  const updateNotifications = useCallback(async (preferences) => {
    setIsUpdatingNotifications(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      setNotificationPreferences(preferences);
      showToast('Notification preferences updated', 'success');
      return { success: true };
    } catch (error) {
      showToast('Failed to update preferences', 'error');
      return { success: false, error };
    } finally {
      setIsUpdatingNotifications(false);
    }
  }, [showToast]);

  const toggleNotification = useCallback((notificationType, channel) => {
    setNotificationPreferences(prev => ({
      ...prev,
      [notificationType]: {
        ...prev[notificationType],
        [channel]: !prev[notificationType][channel]
      }
    }));
  }, []);

  const value = {
    profile,
    notificationPreferences,
    isUpdatingProfile,
    isUpdatingPassword,
    isUpdatingNotifications,
    toast,
    updateProfile,
    updatePassword,
    updateNotifications,
    toggleNotification,
    showToast
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};